import { showLoader } from "action/loader/showLoader";
import AmpService from "analytics/amplitude/amplitudeEvents";
import getFittingProductPriceRange from "api/category/getFittingProductPriceRange";
import classnames from "classnames";
import PAImage from "component/design-system/images/PAImage";
import PAText from "component/design-system/typography/PAText/PAText";
import LoaderSkeleton from "component/loader-skeleton/LoaderSkeleton";
import useScreenSize from "hoc/useScreenSize";
import { RootState } from "interfaces/generics.type";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { currency } from "util/helper/currency";
import HideIf from "util/HideIf";
import { capitalize } from "util/lodash/capitalize";
import { createMakeImageSource } from "util/vehicle/createMakeImageSource";
import { buildFitmentString, createVehicleDescription } from "util/vehicle/createVehicleDescription";
import styles from "./interactiveVehicle.module.scss";

const imageWidth = 950;

function InteractiveVehicle() {
  const [activeHotspot, setActiveHotspot] = useState<number | null>(null);
  const [imageScale, setImageScale] = useState<number>(1.92);
  const [popoverPosition, setPopoverPosition] = useState<{ [key: string]: "left" | "right" }>({});
  const hotspotRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const imgWrapperRef = useRef<HTMLDivElement>(null);
  const [renderedWidth, setRenderedWidth] = useState(imageWidth);
  const [hotspots, setHotspots] = useState([]);

  const { t } = useTranslation("category");
  const [isMobile] = useScreenSize();

  const { selectedVehicle, vehicleAnnotations, vehicleImages } = useSelector(
    (state: RootState) => state.garage,
  );

  const vehicleImageUrl = isMobile ? vehicleImages.medium : vehicleImages.large;
  const angle = vehicleImages.angle || "";

  const dispatch = useDispatch();

  const vehicleDes = createVehicleDescription(selectedVehicle);
  const fitmentOptions = buildFitmentString(selectedVehicle);
  const router = useRouter();
  const { locale, query, asPath } = router;

  // log amplitude event when user views this component
  useEffect(() => {
    setActiveHotspot(null);
    if (!imgWrapperRef.current) return undefined;
    let fired = false;

    if (!("IntersectionObserver" in window)) return undefined;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !fired) {
        fired = true;
        AmpService.vehicleAnnotationsSectionViewed({ query, selectedVehicle });
      }
    }, { threshold: 0.2 });

    observer.observe(imgWrapperRef.current);

    return () => {
      fired = false;
      observer.disconnect();
    };
  }, [asPath]);

  useEffect(() => {
    if (!vehicleAnnotations || !selectedVehicle) return;

    const imageData = vehicleAnnotations?.[angle]?.[0];

    setImageScale(imageData?.image?.width / imageData?.image?.height);

    if (!imageData?.predictions) {
      setHotspots([]);
      return;
    }

    const mapped = imageData.predictions
      .filter((p) => p?.category?.tier1)
      .map((p, index) => ({
        id: index,
        x: p.x_perc,
        y: p.y_perc,
        title: capitalize(p.category.tier1),
        priceRange: null,
        category: p.category,
      }));

    setHotspots(mapped);
  }, [vehicleAnnotations, selectedVehicle, angle]);

  const calculatePopoverPosition = (hotspotId: string | number) => {
    const hotspotElement = hotspotRefs.current[hotspotId];
    if (!hotspotElement) return "right";

    const rect = hotspotElement.getBoundingClientRect();
    const popoverWidth = window.innerWidth >= 768 ? 230 : 180;

    const hotspotCenterX = rect.left + (rect.width / 2);
    const popoverRightEdge = hotspotCenterX + popoverWidth;

    return popoverRightEdge > (window.innerWidth) ? "left" : "right";
  };

  function activateHotspot(id: number) {
    const pos = calculatePopoverPosition(id);

    setPopoverPosition((prev) => ({
      ...prev,
      [id]: pos,
    }));

    setActiveHotspot(id);

    const currentHotspot = hotspots.find((h) => h.id === id);

    if (activeHotspot !== id) {
      AmpService.vehicleAnnotationHovered({ query, selectedVehicle, annotation: Object.fromEntries(Object.entries(currentHotspot?.category).filter(([_, v]) => v)) });
    }

    if (currentHotspot.priceRange) return;

    getFittingProductPriceRange(
      selectedVehicle,
      currentHotspot.category,
    ).then(({ data }) => {
      setHotspots((prev) => prev.map((h) => (h.id === id ? { ...h, productsFound: data.productsFound, priceRange: data.productsFound ? `${locale === "en" ? "CAD" : ""} ${currency(data.minPrice, locale, null)} - ${currency(data.maxPrice, locale, null)} ${locale === "fr" ? "CAD" : ""}` : t("No products") } : h)));
    });
  }

  useEffect(() => {
    function updateSize() {
      if (imgWrapperRef.current) {
        setRenderedWidth(imgWrapperRef.current.offsetWidth);
      }
    }

    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  function handlePopoverClick(hotspot: any) {
    if (!hotspot.productsFound) return;
    AmpService.vehicleAnnotationClicked({ query, selectedVehicle, annotation: Object.fromEntries(Object.entries(hotspot?.category).filter(([_, v]) => v)) });

    dispatch(showLoader());
    import("component/categories-viewer/handler/CategoryViewItemhandler")
      .then((module) => {
        module.categoryViewItemhandler(router, hotspot.category, selectedVehicle, null, { buildQueryParams: "" });
      });
  }

  const calculatedHeight = renderedWidth / imageScale;

  return (
    <div className={styles.interactive_vehicle_container}>
      <PAText component="h3" bold>
        {t("Explore other products in this category")}
      </PAText>

      <div className={styles.car_container}>
        <div ref={imgWrapperRef}>
          <PAImage
            src={vehicleImageUrl}
            alt="interactive image"
            width={renderedWidth}
            height={calculatedHeight}
            className={styles.car_image}
          />
        </div>
        {hotspots.map((hotspot) => (
          <div
            key={hotspot.id}
            className={styles.hotspot_container}
            onMouseEnter={!isMobile ? () => activateHotspot(hotspot.id) : undefined}
            onClick={() => activateHotspot(hotspot.id)}
            onMouseLeave={() => setActiveHotspot(null)}
          >
            <div
              ref={(el) => (hotspotRefs.current[hotspot.id] = el)}
              className={classnames(styles.hotspot_ring, {
                [styles.hotspot_ring_active]: activeHotspot === hotspot.id,
              })}
              style={{
                left: `calc(${hotspot.x}% - 15px)`,
                top: `calc(${hotspot.y}% - 15px)`,
              }}
            >
              <div className={styles.hotspot} />

              <HideIf show={activeHotspot === hotspot.id}>
                <div
                  className={classnames(styles.hotspot_popover, {
                    [styles.popover_left]: popoverPosition[hotspot.id] === "left",
                    [styles.popover_right]: popoverPosition[hotspot.id] === "right",
                    [styles.popover_disable]: hotspot?.productsFound === false,
                  })}
                  onClick={() => handlePopoverClick(hotspot)}
                >
                  <div className={styles.hotspot_info}>
                    <PAText bold className={styles.hotspot_info_title}>{hotspot.title}</PAText>
                    <PAText bold>{hotspot.priceRange}</PAText>
                    <HideIf show={hotspot?.productsFound === undefined}>
                      <LoaderSkeleton height={15} width={110} />
                    </HideIf>
                  </div>
                  <div>
                    <i className={`fa fa-chevron-right ${styles.arrow}`} />
                  </div>
                </div>
              </HideIf>
            </div>
          </div>
        ))}
      </div>

      <div className={styles.vehicle_logo}>
        <PAImage
          src={createMakeImageSource(selectedVehicle)}
          width={45}
          height={45}
          alt={selectedVehicle?.make || "vehicle make"}
        />
        <div className={styles.vehicle_logo_text}>
          <PAText bold className={styles.vehicle}>
            {vehicleDes}
          </PAText>
          <PAText bold className={styles.fitment}>
            {fitmentOptions}
          </PAText>
        </div>
      </div>
    </div>
  );
}

export default InteractiveVehicle;
